import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Divider,
  MenuItem,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Fade,
  Grow,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Add, Image, Delete } from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Import Quill styles
import { uploadImage } from '../api/superAdmin';
import {
  addProduct,
  updateProduct,
  fetchProductById,
  fetchLocations,
  fetchCategories,
  addCategory,
} from '../api/superAdmin';
import OptionInput from './OptionInput';
import StockInput from './StockInput';

interface BackendAttribute {
  name: string;
  values: string[];
}

interface BackendLocation {
  location_name: string;
  stock_type: 'in_store' | 'online';
  stock: number;
}


interface BackendVariant {
  sku: string;
  price: number;
  attributes: BackendAttribute[];
  locations: BackendLocation[];
  image_url?: string;
}

interface BackendProduct {
  product_name: string;
  sku: string;
  description: string;
  price: string;
  supplier_price: string;
  image_url?: string;
  variants: BackendVariant[];
  category: string | null;
}

interface Variant {
  sku: string;
  price: number;
  locations: {
    name: string;
    stocks: {
      stock_type: 'in_store' | 'online';
      stock: number;
    }[];
  }[];
  attributes: { name: string; value: string }[];
  imageUrl?: string;
  imageFile?: File;
  markedForDelete?: boolean;
}


interface Option {
  name: string;
  values: string[];
}

interface StockLocation {
  name: string;
  stocks: {
    stock_type: 'in_store' | 'online';
    stock: number;
  }[];
}


interface Category {
  id: number | null;
  name: string;
}

interface ProductFormProps {
  isEditMode?: boolean;
  productId?: number;
  onSubmitSuccess: () => void;
}



const ProductForm: React.FC<ProductFormProps> = ({
  isEditMode = false,
  productId,
  onSubmitSuccess,
}) => {
  const [productName, setProductName] = useState('');
  const [sku, setSku] = useState('');
  const [price, setPrice] = useState<number | ''>('');
  const [supplierPrice, setSupplierPrice] = useState<number | ''>('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [variants, setVariants] = useState<Variant[]>([]);
  const [locations, setLocations] = useState<StockLocation[]>([]);
  const [options, setOptions] = useState<Option[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [customCategory, setCustomCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [fillAllInStore, setFillAllInStore] = useState<number>(0);
const [fillAllOnline, setFillAllOnline] = useState<number>(0);


const handleApplyStockToAllVariants = () => {
  const updatedVariants = variants.map((variant) => {
    const updatedLocations = variant.locations.map((loc) => ({
      ...loc,
      stocks: loc.stocks.map((stockEntry) => {
        if (stockEntry.stock_type === 'in_store') {
          return { ...stockEntry, stock: fillAllInStore };
        } else if (stockEntry.stock_type === 'online') {
          return { ...stockEntry, stock: fillAllOnline };
        }
        return stockEntry;
      }),
    }));
    return {
      ...variant,
      locations: updatedLocations,
    };
  });

  setVariants(updatedVariants);
};


  const calculateMarkup = (retailPrice: number, supplierPrice: number) => {
    return ((retailPrice - supplierPrice) / supplierPrice) * 100;
  };

  const calculateMargin = (retailPrice: number, supplierPrice: number) => {
    return ((retailPrice - supplierPrice) / retailPrice) * 100;
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const locationsData = await fetchLocations();
        setLocations(locationsData.locations || []);

        const categoriesData = await fetchCategories();
        const backendCategories: Category[] = categoriesData.categories.map((cat: Category) => ({
          id: cat.id,
          name: cat.name,
        }));

        setCategories([{ id: null, name: 'Uncategorized' }, ...backendCategories]);

        if (isEditMode && productId) {
          const product: BackendProduct = await fetchProductById(productId);

          // Set basic fields
          setProductName(product.product_name);
          setSku(product.sku);
          setPrice(Number(product.price));
          setSupplierPrice(Number(product.supplier_price));
          setDescription(product.description || '');
          setImageUrl(product.image_url ?? null);

          // Set category
          const matchingCategory = backendCategories.find((cat) => cat.name === product.category);
          setSelectedCategory(matchingCategory?.id || null);

          // Process variants
          const processedVariants = product.variants.map((variant) => ({
            sku: variant.sku,
            price: variant.price,
            locations: variant.locations.reduce((acc: StockLocation[], loc) => {
  const existing = acc.find((l) => l.name === loc.location_name);
  if (existing) {
    existing.stocks.push({ stock_type: loc.stock_type, stock: loc.stock });
  } else {
    acc.push({
      name: loc.location_name,
      stocks: [{ stock_type: loc.stock_type, stock: loc.stock }],
    });
  }
  return acc;
}, []),

            attributes: variant.attributes.flatMap((attr) =>
              attr.values.map((value) => ({
                name: attr.name,
                value: value,
              }))
            ),
            imageUrl: variant.image_url,
          }));
          setVariants(processedVariants);

          // Derive options from variants
          const optionMap: Record<string, Set<string>> = {};
          product.variants.forEach((variant) => {
            variant.attributes.forEach((attr) => {
              if (!optionMap[attr.name]) {
                optionMap[attr.name] = new Set();
              }
              attr.values.forEach((value) => optionMap[attr.name].add(value));
            });
          });

          const derivedOptions = Object.entries(optionMap).map(([name, values]) => ({
            name,
            values: Array.from(values),
          }));
          setOptions(derivedOptions);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    fetchInitialData();
  }, [isEditMode, productId]);

  useEffect(() => {
    if (!isEditMode && productName) {
      const baseSku = "tb";
      setSku(`${baseSku}-${Date.now()}`);
    }
  }, [productName, isEditMode]);

  const generateOptionCombinations = (options: Option[]): Array<Array<{ name: string; value: string }>> => {
    if (!options.length) return [[]];
    return options.reduce<Array<Array<{ name: string; value: string }>>>(
      (acc, option) =>
        acc.flatMap((comb) =>
          option.values.map((value) => [...comb, { name: option.name, value }])
        ),
      [[]]
    );
  };

 useEffect(() => {
  const generateVariants = () => {
    const combinations = generateOptionCombinations(options);
    const defaultStocks: { stock_type: 'in_store' | 'online'; stock: number }[] = [
      { stock_type: 'in_store', stock: 0 },
      { stock_type: 'online', stock: 0 },
    ];

    const newVariants: Variant[] = combinations.map((combination) => {
      const skuSuffix = combination.map((attr) => attr.value).join('-');
      const variantSku = `${sku}-${skuSuffix}`;

      const existing = variants.find((v) =>
        combination.every((cAttr) =>
          v.attributes.some((vAttr) => vAttr.name === cAttr.name && vAttr.value === cAttr.value)
        )
      );

      return (
        existing || {
          sku: variantSku,
          price: Number(price) || 0,
          locations: locations.map((loc) => ({
            name: loc.name,
            stocks: [...defaultStocks],
          })),
          attributes: combination,
          imageUrl: undefined,
        }
      );
    });

    setVariants(newVariants);
  };

  generateVariants();
}, [options, sku, price, locations]);


  const handleAddCustomCategory = async () => {
    if (customCategory.trim() && !categories.some((cat) => cat.name === customCategory)) {
      try {
        const newCategory = await addCategory(customCategory);
        const newCategoryObj = { id: newCategory.id, name: newCategory.name };
        setCategories([...categories, newCategoryObj]);
        setSelectedCategory(newCategory.id);
        setCustomCategory('');
      } catch (err) {
        console.error('Error adding category:', err);
        alert('Failed to add category. Please try again.');
      }
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImage(file);
      try {
        const uploadedImageUrl = await uploadImage(file);
        setImageUrl(uploadedImageUrl);
      } catch (err) {
        console.error('Failed to upload main image:', err);
        alert('Failed to upload main image.');
      }
    }
  };

  const handleDeleteVariant = (index: number) => {
    const updatedVariants = variants.filter((_, i) => i !== index);
    setVariants(updatedVariants);
  };

  const handleVariantImageChange = async (index: number, file: File) => {
    const updatedVariants = [...variants];
    updatedVariants[index].imageFile = file;

    try {
      const uploadedVariantImageUrl = await uploadImage(file);
      updatedVariants[index].imageUrl = uploadedVariantImageUrl;
      setVariants(updatedVariants);
    } catch (err) {
      console.error('Failed to upload variant image:', err);
      alert('Failed to upload variant image.');
    }
  };

  const handleSubmit = async () => {
  setLoading(true);

  try {
    let mainImageUrl: string | undefined = imageUrl || undefined;

    if (image instanceof File) {
      mainImageUrl = await uploadImage(image);
    }

    const variantsWithImages = await Promise.all(
      variants
        .filter((v) => !v.markedForDelete)
        .map(async (variant) => {
          let variantImageUrl: string | undefined = variant.imageUrl || undefined;
          if (variant.imageFile) {
            variantImageUrl = await uploadImage(variant.imageFile);
          }

          // derive a clean name for saving
          const variantName =
            variant.attributes.map((a) => a.value).join(" ") || variant.sku;

          return {
            sku: variant.sku,
            price: variant.price,
            image_url: variantImageUrl,
            variant_name: variantName, // ✅ now saving variant_name
            attributes: Object.entries(
              variant.attributes.reduce((acc, attr) => {
                if (!acc[attr.name]) acc[attr.name] = [];
                if (!acc[attr.name].includes(attr.value)) {
                  acc[attr.name].push(attr.value);
                }
                return acc;
              }, {} as Record<string, string[]>)
            ).map(([name, values]) => ({ name, values })),
            locations: variant.locations,
          };
        })
    );

    const productPayload = {
      name: productName,
      sku,
      price: Number(price) || 0,
      supplier_price: Number(supplierPrice) || 0,
      description: description || undefined,
      category_id: selectedCategory,
      image_url: mainImageUrl,
      variants: variantsWithImages,
    };

    if (isEditMode && productId) {
      await updateProduct(productId, productPayload);
    } else {
      await addProduct(productPayload);
    }

    onSubmitSuccess();
  } catch (error) {
    console.error("Error submitting product:", error);
    alert("Failed to submit product. Please try again.");
  } finally {
    setLoading(false);
  }
};


  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        {isEditMode ? 'Edit Product' : 'Add Product'}
      </Typography>

      {/* Product Details Section */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Product Details" />
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Product Name"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="SKU"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Supplier Price"
              type="number"
              value={supplierPrice}
              onChange={(e) => setSupplierPrice(Number(e.target.value))}
              fullWidth
              size="small"
            />
            <TextField
              label="Retail Price"
              type="number"
              value={price}
              onChange={(e) => setPrice(Number(e.target.value))}
              fullWidth
              size="small"
            />
            <TextField
              label="Markup (%)"
              type="number"
              value={calculateMarkup(Number(price), Number(supplierPrice))}
              disabled
              fullWidth
              size="small"
            />
            <TextField
              label="Margin (%)"
              type="number"
              value={calculateMargin(Number(price), Number(supplierPrice))}
              disabled
              fullWidth
              size="small"
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Description</Typography>
            <ReactQuill
              value={description}
              onChange={setDescription}
              modules={{
                toolbar: [
                  [{ header: [1, 2, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                  ['link', 'image'],
                  ['clean'],
                ],
              }}
              theme="snow"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Category Section */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Category" />
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              select
              label="Category"
              value={selectedCategory ?? ''}
              onChange={(e) => setSelectedCategory(e.target.value ? Number(e.target.value) : null)}
              fullWidth
              size="small"
            >
              {categories.map((category) => (
                <MenuItem key={category.id ?? 'uncategorized'} value={category.id ?? ''}>
                  {category.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="New Category"
              value={customCategory}
              onChange={(e) => setCustomCategory(e.target.value)}
              size="small"
            />
            <Button onClick={handleAddCustomCategory} variant="contained" size="small">
              Add
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Main Image Upload */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Main Image" />
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton component="label">
              <Image />
              <input type="file" hidden onChange={handleFileChange} />
            </IconButton>
            {imageUrl && (
              <img
                src={imageUrl}
                alt="Product Preview"
                style={{ width: 80, height: 80, objectFit: 'contain' }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Options Section */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Options" />
        <CardContent>
          {options.map((option, index) => (
            <Grow in={true} key={index}>
              <Box sx={{ mb: 2 }}>
                <OptionInput
                  name={option.name}
                  values={option.values}
                  onNameChange={(newName) => {
                    const updatedOptions = [...options];
                    updatedOptions[index].name = newName;
                    setOptions(updatedOptions);
                  }}
                  onValuesChange={(newValues) => {
                    const updatedOptions = [...options];
                    updatedOptions[index].values = newValues;
                    setOptions(updatedOptions);
                  }}
                  onDelete={() => {
                    const updatedOptions = options.filter((_, i) => i !== index);
                    setOptions(updatedOptions);

                    // Clean up variants that use this option
                    const updatedVariants = variants.filter((variant) =>
                      !variant.attributes.some((attr) => attr.name === option.name)
                    );
                    setVariants(updatedVariants);
                  }}
                />
              </Box>
            </Grow>
          ))}
          <Button
            variant="outlined"
            onClick={() => setOptions([...options, { name: '', values: [] }])}
            sx={{ mt: 1 }}
          >
            Add Option
          </Button>
        </CardContent>
      </Card>

      {/* Global Fill All Stock Controls */}
<Card sx={{ mb: 3 }}>
  <CardHeader title="Fill Stock for All Variants" />
  <CardContent>
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
      <TextField
        label="Fill All In-Store"
        type="number"
        size="small"
        value={fillAllInStore}
        onChange={(e) => setFillAllInStore(Number(e.target.value))}
      />
      <TextField
        label="Fill All Online"
        type="number"
        size="small"
        value={fillAllOnline}
        onChange={(e) => setFillAllOnline(Number(e.target.value))}
      />
      <Button
        variant="outlined"
        onClick={handleApplyStockToAllVariants}
      >
        Apply to All Variants
      </Button>
    </Box>
  </CardContent>
</Card>


      {/* Variants Section */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Variants" />
        <CardContent>
          {variants.map((variant, index) => (
            <Fade in={true} key={index}>
              <Accordion sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1">
                      {variant.sku} - ${variant.price}
                    </Typography>
                    {variant.attributes.map((attr) => (
                      <Chip
                        key={`${attr.name}-${attr.value}`}
                        label={`${attr.name}: ${attr.value}`}
                        size="small"
                      />
                    ))}
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteVariant(index);
                      }}
                      sx={{ ml: 'auto' }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    label="SKU"
                    value={variant.sku}
                    onChange={(e) => {
                      const newVariants = [...variants];
                      newVariants[index].sku = e.target.value;
                      setVariants(newVariants);
                    }}
                    fullWidth
                    margin="normal"
                    size="small"
                  />
                  <TextField
                    label="Price"
                    type="number"
                    value={variant.price}
                    onChange={(e) => {
                      const newVariants = [...variants];
                      newVariants[index].price = Number(e.target.value);
                      setVariants(newVariants);
                    }}
                    fullWidth
                    margin="normal"
                    size="small"
                  />
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">Variant Image</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <IconButton component="label">
                        <Image />
                        <input
                          type="file"
                          hidden
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              handleVariantImageChange(index, e.target.files[0]);
                            }
                          }}
                        />
                      </IconButton>
                      {variant.imageUrl && (
                        <img
                          src={variant.imageUrl}
                          alt="Variant Preview"
                          style={{ width: 60, height: 60, objectFit: 'contain' }}
                        />
                      )}
                    </Box>
                  </Box>
                  <StockInput
                    locations={variant.locations}
                    allLocations={locations}
                    onLocationsChange={(updatedLocations) => {
                      const newVariants = [...variants];
                      newVariants[index].locations = updatedLocations;
                      setVariants(newVariants);
                    }}
                  />
                </AccordionDetails>
              </Accordion>
            </Fade>
          ))}
        </CardContent>
      </Card>

      {/* Submit Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {isEditMode ? 'Update Product' : 'Create Product'}
        </Button>
      </Box>
    </Box>
  );
};

export default ProductForm;